// pages/PatientRecords.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { ArrowLeft, Stethoscope } from "lucide-react";

import { db, auth } from "../firebase";
import MedicalRecordsSection from "../components/patient/MedicalRecordsSection";
import MedicalRecordModal from "../components/patient/MedicalRecordModal";

export default function PatientRecords() {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);

  const [medicalRecords, setMedicalRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Load medical records for this patient
  useEffect(() => {
    if (!user) return;

    const qRec = query(
      collection(db, "medicalRecords"),
      where("patientId", "==", user.uid)
    );

    const unsub = onSnapshot(qRec, (snap) => {
      setMedicalRecords(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return () => unsub();
  }, [user]);

  if (!user) {
    return (
      <div
        style={{
          padding: 16,
          maxWidth: 420,
          margin: "0 auto",
          fontFamily:
            '-apple-system,BlinkMacSystemFont,"SF Pro Text","SF Pro Display",system-ui,"Segoe UI",Roboto,Helvetica,Arial,sans-serif',
          fontSize: 14,
          color: "#4B5563",
        }}
      >
        Duhet të hysh në llogarinë tënde për të parë rekordet mjekësore.
      </div>
    );
  }

  const totalRecords = medicalRecords.length;

  return (
    <div
      style={{
        padding: 16,
        maxWidth: 480,
        margin: "0 auto",
        fontFamily:
          '-apple-system,BlinkMacSystemFont,"SF Pro Text","SF Pro Display",system-ui,"Segoe UI",Roboto,Helvetica,Arial,sans-serif',
      }}
    >
      {/* TOP HEADER (back + title) */}
      <header
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          {/* back button */}
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={{
              width: 32,
              height: 32,
              borderRadius: 999,
              border: "1px solid #E5E7EB",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#FFFFFF",
              cursor: "pointer",
            }}
          >
            <ArrowLeft style={{ width: 18, height: 18, color: "#4B5563" }} />
          </button>

          {/* title + subtitle */}
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "#0F172A",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Stethoscope
                style={{ width: 18, height: 18, color: "#0F766E" }}
              />
              Rekordet mjekësore
            </span>
            <span
              style={{
                fontSize: 12,
                color: "#6B7280",
              }}
            >
              Vizitat, diagnozat dhe raportet që janë ruajtur nga mjekët e tu.
            </span>
          </div>

          <div style={{ flex: 1 }} />

          {/* small pill with total records */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 8px",
              borderRadius: 999,
              border: "1px solid #E5E7EB",
              background: "#F9FAFB",
              fontSize: 11,
              color: "#4B5563",
              whiteSpace: "nowrap",
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: 999,
                background: totalRecords > 0 ? "#22C55E" : "#9CA3AF",
              }}
            />
            <span>{totalRecords} rekord{totalRecords === 1 ? "" : "e"}</span>
          </div>
        </div>
      </header>

      {/* MEDICAL RECORDS LIST (premium card we built) */}
      <MedicalRecordsSection
        records={medicalRecords}
        setSelectedRecord={setSelectedRecord}
      />

      {/* MODAL WITH FULL RECORD DETAILS */}
      {selectedRecord && (
        <MedicalRecordModal
          record={selectedRecord}
          onClose={() => setSelectedRecord(null)}
        />
      )}
    </div>
  );
}
